import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  TextField,
  Chip,
  Box
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ModeEditIcon from '@mui/icons-material/ModeEdit';

import { getPeople, getReciversUser } from "../../services/backend/UserService";
import { setPeople, setUsers } from "../../redux/slices/userSlice";
import UserModal from "../generic/UserModal";
import { sortAsc } from "../../functions";

const SelectUser = ({
  freeSolo,
  multiple,
  userType,
  value = null,
  setValue,
  disabled,
  noAdd,
  noedit,
  placeholder,
  filter,
  onChange,
  required,
  error,
  helperText,
  // Optional, for callers that need more than a name in the list: a predicate
  // narrowing the options, and a second line shown on the right of each one.
  optionFilter,
  renderSecondary,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);
  const users = useSelector((state) => {
    if (!state.user.users) return null;
    if (!filter) return state.user.users;
    return state.user.users.filter(u => u.roles?.some(r => filter.includes(r)));
  });
  const people = useSelector((state) => state.user.people);

  // Internal user records are created and edited in /admin only, never from a
  // picker. Deliberate: user administration stays one module with one flow, and
  // handleUser authorizes it by permission, so an inline form would 403 for most
  // of the people who can see it anyway.
  //
  // The external address book (`people`) is a different thing: those are
  // correspondence contacts, not accounts, and anyone filing has to be able to
  // add one without leaving the form.
  const canEditContact = userType !== "users";

  const [userModal, setUserModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [titleModal, setTitleModal] = useState();

  useEffect(() => {
    if (!users) {
      (async () => {
        const response = await getReciversUser(user.token);
        dispatch(setUsers(response.users.sort(sortAsc('name'))));
      })();
    }
    if (!people) {
      (async () => {
        const response = await getPeople(user.token);
        dispatch(setPeople(response.people.sort(sortAsc('name'))));
      })();
    }
  }, []); // eslint-disable-line

  if (!users || !people) return null;

  const base = userType === "users" ? users : userType === "people" ? people : [];
  const options = optionFilter ? base.filter(optionFilter) : base;

  return (
    <Box sx={{ flexDirection: "row", display: "flex", alignItems: "center", flex: 1 }}>
      <Autocomplete
        freeSolo={freeSolo}
        multiple={multiple}
        options={options}
        value={value || (multiple ? [] : null)}
        getOptionLabel={(option) => `${option.name} ${option.lastName} <${option.email}>`}
        isOptionEqualToValue={(option, value) => {
          if (typeof option === "string" || typeof value === "string") return option === value;
          return option.email === value?.email;
        }}
        disabled={disabled}
        // sx={{ width: !multiple && value ? "calc(100% - 80px)" : "calc(100% - 50px)" }}
        sx={{ flex: 1, mr: 1 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={placeholder}
            required={required}
            error={error}
            helperText={helperText}
          />
        )}
        onChange={onChange}
        renderOption={(props, option) => (
          <li {...props} key={userType === "users" ? option.userId : option.peopleId}>
            {renderSecondary ? (
              <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {`${option.name} ${option.lastName} <${option.email}>`}
                </Box>
                <Box sx={{ color: "text.secondary", fontSize: 13, whiteSpace: "nowrap" }}>
                  {renderSecondary(option)}
                </Box>
              </Box>
            ) : (
              `${option.name} ${option.lastName} <${option.email}>`
            )}
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const isString = typeof option === "string";
            return (
              <Chip
                key={index}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  if (noedit || isString || !canEditContact) return;
                  setOpenModal(true);
                  setTitleModal("Modificar Externo");
                  setUserModal(option);
                }}
                label={isString ? (option) : (
                  <div>{`${option.name} ${option.lastName}`} <small>{`<${option.email}>`}</small> </div>
                )}
                {...getTagProps({ index })}
              />
            )
          })
        }
      />
      {!noedit && !noAdd && canEditContact
        && <PersonAddIcon
          color={noedit ? "disabled" : "primary"}
          onClick={() => {
            if (noedit) return;
            setOpenModal(true);
            setTitleModal("Crear Externo");
          }}
          sx={{ cursor: "pointer" }}
        />
      }
      {!multiple && value && !noedit && canEditContact &&
        <ModeEditIcon
          color={noedit ? "disabled" : "primary"}
          onClick={() => {
            if (noedit) return;
            setOpenModal(true);
            setTitleModal("Modificar Externo");
            setUserModal(value);
          }}
          sx={{
            // position: "absolute",
            // right: "45px",
            // top: "15px",
            cursor: "pointer",
          }}
        />
      }
      <UserModal
        title={titleModal}
        open={openModal}
        user={userModal}
        userType={userType}
        onSubmit={(userData) => {
          let vals = userData;

          if (multiple) {
            vals = [...value];
            if (!userData.isNew) {
              for (let i = 0; i < vals.length; i++) {
                if (vals[i].userId === userData.userId || vals[i].peopleId === userData.peopleId) {
                  vals[i] = userData;
                  break;
                }
              }
            } else {
              vals.push(userData);
            }
          }

          setValue(vals);
        }}
        onClose={() => {
          setOpenModal(false);
          setUserModal(undefined);
          setTitleModal(undefined);
        }}
      />
    </Box>
  );
};

export default SelectUser;
